// The 16 hats of §7.3 (render/bean/hats.ts per §12.4): a fixed primitive recipe per hat, built
// in head-anchor space (origin = the §12.4 head anchor at body (0, 1.05, 0); +y up, +z = face).
// The head's top is at y ≈ +0.05 and its radius is ≈ 0.24 at y = −0.03. Animated bits (a spinning
// propeller, a bobbing halo) go on the HAT_ANIM part and are driven by the rig.
import {
  BoxGeometry, ConeGeometry, CylinderGeometry, IcosahedronGeometry, SphereGeometry, TorusGeometry, Vector3,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { MODE, PAT, type PartGeometryBuilder } from '../materials/partGeometry';
import { band, brim, curve, extrude, M, smoothLathe, taperedTube } from './geo';

export type HatAnim = 'none' | 'spin' | 'bob' | 'wiggle' | 'swim' | 'jiggle';

export interface HatInfo {
  /** Height of the hat's top above the anchor (crowns and the local chevron stack above it). */
  top: number;
  /** Extra scale about the head anchor for hats that read too small at gameplay distance. */
  scale?: number;
  anim: HatAnim;
  /** Pivot of the animated part in anchor space. */
  pivot: [number, number, number];
}

export interface HatParts { head: number; anim: number }

type Recipe = (b: PartGeometryBuilder, p: HatParts) => HatInfo;

const C = {
  ink: '#2A1A3A', cream: '#FFF6E9', white: '#FFFFFF', pink: '#FF4F9A', yellow: '#FFC93C', red: '#FF5A4E',
  blue: '#4CB8FF', green: '#3DDC97', orange: '#FF8A1F', gold: '#FFD24A', brass: '#E8B64C', steel: '#B9C4D0',
  black: '#2B2433', ivory: '#FFF1C9', tan: '#C98A4B', brown: '#6B3E2E', navy: '#3B2F8F', purple: '#6E4BD8',
  lavender: '#C7B8FF', peach: '#FFB59A', leaf: '#4CC46A', water: '#7FD4FF', fish: '#FF8A2A',
};

const NONE: HatInfo['pivot'] = [0, 0, 0];

/** A rounded n-lobed blossom outline (radius r) for extrusion. */
function flowerPoints(r: number, lobes: number): [number, number][] {
  const out: [number, number][] = [];
  const n = lobes * 4;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const k = 0.62 + 0.38 * Math.abs(Math.cos((a * lobes) / 2));
    out.push([Math.cos(a) * r * k, Math.sin(a) * r * k]);
  }
  return out;
}

/** Level of detail for the recipe being built (1 = High; Low and Potato build at ~0.6). */
let lod = 1;
/** A segment count scaled by the current level of detail. */
function s(n: number): number {
  return Math.max(3, Math.round(n * lod));
}

const RECIPES: Readonly<Record<number, Recipe>> = {
  // 1 Party cone (§12.4 example): ConeGeometry r 0.18 h 0.4, stripes, pom-pom.
  1: (b, p) => {
    const tilt = M(0.03, -0.03, 0, 0, 0, -0.2);
    const cone = new ConeGeometry(0.18, 0.4, s(24), 6);
    cone.translate(0, 0.2, 0);
    cone.applyMatrix4(tilt);
    b.add(cone, null, { part: p.head, mode: MODE.VCOLOR, color: C.pink, color2: C.yellow, pat: PAT.STRIPES, freq: 11, axis: 1 });
    const pom = new IcosahedronGeometry(0.065, 2);
    pom.translate(0, 0.41, 0);
    pom.applyMatrix4(tilt);
    b.add(pom, null, { part: p.head, mode: MODE.VCOLOR, color: C.yellow });
    return { top: 0.42, anim: 'none', pivot: NONE };
  },
  // 2 Propeller beanie: four panels, a little brim, a spinning two-blade propeller.
  2: (b, p) => {
    const panels = [C.red, C.yellow, C.blue, C.green];
    for (let i = 0; i < 4; i++) {
      const g = new SphereGeometry(0.265, s(8), s(6), (i * Math.PI) / 2, Math.PI / 2, 0, Math.PI / 2);
      b.add(g, M(0, -0.07, 0, 0, 0, 0, 1, 0.68, 1), { part: p.head, mode: MODE.VCOLOR, color: panels[i] });
    }
    const visor = brim(0.2, 0.36, 0.025, s(24), (_a, t) => -0.06 - t * 0.02, (a) => (Math.cos(a) > 0.2 ? 1 : 0.62));
    b.add(visor, M(0, 0, 0.02), { part: p.head, mode: MODE.VCOLOR, color: C.red });
    b.add(new SphereGeometry(0.035, s(10), s(8)), M(0, 0.115, 0), { part: p.head, mode: MODE.VCOLOR, color: C.yellow });
    b.add(new CylinderGeometry(0.014, 0.014, 0.08, s(8)), M(0, 0.15, 0), { part: p.head, mode: MODE.VCOLOR, color: C.steel, outline: 0.5 });
    // Propeller (anim part, spinning about the hub).
    b.add(new SphereGeometry(0.03, s(10), s(8)), M(0, 0.195, 0), { part: p.anim, mode: MODE.VCOLOR, color: C.yellow });
    for (const side of [-1, 1]) {
      b.add(new SphereGeometry(1, s(10), s(6)), M(side * 0.13, 0.195, 0, side * 0.35, 0, 0, 0.13, 0.012, 0.045), { part: p.anim, mode: MODE.VCOLOR, color: side < 0 ? C.red : C.blue, outline: 0.7 });
    }
    return { top: 0.2, anim: 'spin', pivot: [0, 0.195, 0] };
  },
  // 3 Top hat: black crown, wide brim, pink band.
  3: (b, p) => {
    const tilt = M(-0.01, -0.02, 0, 0, 0, 0.12);
    const crown = new CylinderGeometry(0.19, 0.18, 0.34, s(28), 2);
    crown.translate(0, 0.17, 0);
    crown.applyMatrix4(tilt);
    b.add(crown, null, { part: p.head, mode: MODE.VCOLOR, color: C.black });
    const bandG = new CylinderGeometry(0.192, 0.186, 0.07, s(28), 1, true);
    bandG.translate(0, 0.06, 0);
    bandG.applyMatrix4(tilt);
    b.add(bandG, null, { part: p.head, mode: MODE.VCOLOR, color: C.pink, outline: 0 });
    const br = brim(0.17, 0.32, 0.028, s(32), (a, t) => t * t * 0.05 * Math.abs(Math.sin(a)));
    br.applyMatrix4(tilt);
    b.add(br, null, { part: p.head, mode: MODE.VCOLOR, color: C.black });
    return { top: 0.36, anim: 'none', pivot: NONE };
  },
  // 4 Traffic cone (Gary's hat): orange cone with two white reflective bands on a square base.
  4: (b, p) => {
    const tilt = M(0, -0.01, 0, 0.06, 0, -0.1);
    const segs: [number, number, number, string][] = [
      [0.0, 0.12, 0.2, C.orange], [0.12, 0.18, 0.2, C.white], [0.18, 0.28, 0.2, C.orange], [0.28, 0.33, 0.2, C.white], [0.33, 0.48, 0.2, C.orange],
    ];
    const rAt = (y: number): number => 0.2 * (1 - y / 0.5) + 0.018;
    for (const [y0, y1, , col] of segs) {
      const g = new CylinderGeometry(rAt(y1), rAt(y0), y1 - y0, s(24), 1, y1 < 0.48);
      g.translate(0, (y0 + y1) / 2, 0);
      g.applyMatrix4(tilt);
      b.add(g, null, { part: p.head, mode: MODE.VCOLOR, color: col });
    }
    const base = new RoundedBoxGeometry(0.44, 0.05, 0.44, 2, 0.02);
    base.translate(0, -0.015, 0);
    base.applyMatrix4(tilt);
    b.add(base, null, { part: p.head, mode: MODE.VCOLOR, color: '#E5641A' });
    return { top: 0.5, anim: 'none', pivot: NONE };
  },
  // 5 Chef toque: a pleated band and a puffy cloud top.
  5: (b, p) => {
    const bandG = new CylinderGeometry(0.235, 0.225, 0.13, s(28), 1);
    bandG.translate(0, 0.03, 0);
    b.add(bandG, null, { part: p.head, mode: MODE.VCOLOR, color: C.white, color2: '#E9EEF5', pat: PAT.WEDGES, around: 28 });
    const puffs: [number, number, number, number][] = [
      [0, 0.24, 0, 0.17], [0.13, 0.19, 0.05, 0.12], [-0.13, 0.19, 0.05, 0.12], [0.05, 0.2, -0.13, 0.12], [-0.08, 0.2, -0.12, 0.12], [0.02, 0.2, 0.14, 0.11],
    ];
    for (const [x, y, z, r] of puffs) b.add(new SphereGeometry(r, s(12), s(9)), M(x, y, z), { part: p.head, mode: MODE.VCOLOR, color: C.white });
    return { top: 0.42, anim: 'none', pivot: NONE };
  },
  // 6 Viking helmet: steel dome, brass band with rivets, nose guard and two curved ivory horns.
  6: (b, p) => {
    b.add(new SphereGeometry(0.27, s(24), s(12), 0, Math.PI * 2, 0, Math.PI / 2), M(0, -0.07, 0, 0, 0, 0, 1, 0.82, 1), { part: p.head, mode: MODE.VCOLOR, color: C.steel });
    const ring = new TorusGeometry(0.262, 0.03, s(6), s(30));
    ring.rotateX(Math.PI / 2);
    b.add(ring, M(0, -0.06, 0), { part: p.head, mode: MODE.GOLD, color: C.brass });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      b.add(new IcosahedronGeometry(0.02, 0), M(Math.sin(a) * 0.29, -0.06, Math.cos(a) * 0.29), { part: p.head, mode: MODE.VCOLOR, color: C.ivory, outline: 0.4 });
    }
    b.add(new RoundedBoxGeometry(0.05, 0.16, 0.03, 2, 0.012), M(0, -0.12, 0.27, -0.18, 0, 0), { part: p.head, mode: MODE.GOLD, color: C.brass });
    for (const side of [-1, 1]) {
      const c = curve([[side * 0.22, 0.04, 0], [side * 0.36, 0.08, 0.02], [side * 0.44, 0.2, 0.0], [side * 0.43, 0.34, -0.04]]);
      b.add(taperedTube(c, 0.07, 0.012, s(16), s(10)), null, { part: p.head, mode: MODE.VCOLOR, color: C.ivory });
    }
    return { top: 0.3, anim: 'none', pivot: NONE };
  },
  // 7 Duck floatie: a yellow inflatable ring with a rubber-duck head and an orange beak.
  7: (b, p) => {
    const ring = new TorusGeometry(0.25, 0.085, s(10), s(28));
    ring.rotateX(Math.PI / 2);
    b.add(ring, M(0, 0, 0), { part: p.anim, mode: MODE.VCOLOR, color: '#FFD93C', color2: C.white, pat: PAT.WEDGES, around: 10 });
    b.add(new SphereGeometry(0.11, s(12), s(9)), M(0, 0.1, 0.29), { part: p.anim, mode: MODE.VCOLOR, color: '#FFD93C' });
    b.add(new SphereGeometry(1, s(12), s(8)), M(0, 0.08, 0.4, 0, 0, 0, 0.07, 0.03, 0.06), { part: p.anim, mode: MODE.VCOLOR, color: C.orange });
    for (const side of [-1, 1]) b.add(new SphereGeometry(0.02, s(8), s(6)), M(side * 0.055, 0.14, 0.375), { part: p.anim, mode: MODE.VCOLOR, color: C.ink, outline: 0 });
    b.add(new SphereGeometry(1, s(10), s(8)), M(0, 0.07, 0.12, 0.6, 0, 0, 0.05, 0.03, 0.08), { part: p.anim, mode: MODE.VCOLOR, color: '#FFD93C' });
    return { top: 0.22, anim: 'bob', pivot: [0, -0.02, 0] };
  },
  // 8 Cowboy hat: creased crown, brim curled up at the sides, dark band.
  8: (b, p) => {
    const crown = smoothLathe([[0.2, 0], [0.2, 0.08], [0.18, 0.18], [0.12, 0.22], [0.0, 0.19]], s(10), s(24));
    b.add(crown, M(0, -0.02, 0, 0, 0, 0, 1, 1, 0.92), { part: p.head, mode: MODE.VCOLOR, color: C.tan });
    const bandG = new CylinderGeometry(0.203, 0.203, 0.04, s(24), 1, true);
    b.add(bandG, M(0, 0.01, 0, 0, 0, 0, 1, 1, 0.92), { part: p.head, mode: MODE.VCOLOR, color: C.brown, outline: 0 });
    const br = brim(0.17, 0.42, 0.025, s(36), (a, t) => -0.02 + Math.pow(t, 2.2) * 0.17 * Math.pow(Math.abs(Math.sin(a)), 1.5) - t * 0.03 * Math.max(0, Math.cos(a)));
    b.add(br, M(0, -0.01, 0), { part: p.head, mode: MODE.VCOLOR, color: '#B97A3E' });
    return { top: 0.21, anim: 'none', pivot: NONE };
  },
  // 9 Wizard hat: a tall, bent, starry cone on a wide brim.
  9: (b, p) => {
    const br = brim(0.17, 0.38, 0.025, s(36), (a, t) => -0.03 - t * t * 0.02 * Math.cos(a * 2));
    b.add(br, null, { part: p.head, mode: MODE.VCOLOR, color: C.navy });
    const c = curve([[0, -0.01, 0], [0, 0.2, 0], [0.02, 0.38, -0.04], [0.14, 0.52, -0.1], [0.24, 0.5, -0.12]]);
    b.add(taperedTube(c, 0.21, 0.012, s(22), s(16)), null, { part: p.head, mode: MODE.VCOLOR, color: C.purple, color2: C.gold, pat: PAT.STARS, around: 7, freq: 9 });
    b.add(new IcosahedronGeometry(0.035, 1), M(0.25, 0.5, -0.12), { part: p.head, mode: MODE.GOLD, color: C.gold });
    return { top: 0.55, anim: 'none', pivot: NONE };
  },
  // 10 Flower crown: a leafy vine ring with seven five-petal flowers.
  10: (b, p) => {
    const vine = new TorusGeometry(0.255, 0.018, s(6), s(40));
    vine.rotateX(Math.PI / 2);
    b.add(vine, M(0, -0.03, 0), { part: p.head, mode: MODE.VCOLOR, color: C.leaf, outline: 0.5 });
    const petals = [C.pink, C.white, C.lavender, C.peach, C.pink, C.white, C.lavender];
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + 0.2;
      const cx = Math.sin(a) * 0.262;
      const cz = Math.cos(a) * 0.262;
      // One extruded five-lobed blossom per flower, facing outward.
      const blossom = extrude(flowerPoints(0.058, 5), 0.026, 0);
      b.add(blossom, M(cx, -0.02, cz, 0, a, (i * 0.7) % 1.2), { part: p.head, mode: MODE.VCOLOR, color: petals[i], outline: 0.6 });
      b.add(new SphereGeometry(0.024, s(8), s(6)), M(cx * 1.1, -0.02, cz * 1.1), { part: p.head, mode: MODE.VCOLOR, color: C.yellow, outline: 0.5 });
      const la = a + Math.PI / 7;
      b.add(new SphereGeometry(1, s(6), s(4)), M(Math.sin(la) * 0.27, -0.035, Math.cos(la) * 0.27, 0, la, 0.5, 0.05, 0.016, 0.028), { part: p.head, mode: MODE.VCOLOR, color: C.leaf, outline: 0.5 });
    }
    return { top: 0.06, anim: 'none', pivot: NONE, scale: 1.12 };
  },
  // 11 Fishbowl: a glass bowl of water on the head with a goldfish doing laps.
  11: (b, p) => {
    const bowl = smoothLathe([[0.12, 0.0], [0.22, 0.05], [0.26, 0.16], [0.24, 0.26], [0.16, 0.37], [0.13, 0.4]], s(16), s(28));
    b.add(bowl, M(0, 0.0, 0), { part: p.head, mode: MODE.GLASS, color: '#D8F3FF', alpha: 0.03 });
    const water = smoothLathe([[0, 0.025], [0.11, 0.025], [0.21, 0.07], [0.25, 0.17], [0.235, 0.29], [0.0, 0.29]], s(12), s(24));
    b.add(water, null, { part: p.head, mode: MODE.GLASS, color: C.water, alpha: 0.6, outline: 0 });
    b.add(new CylinderGeometry(0.13, 0.15, 0.03, s(20)), M(0, 0.0, 0), { part: p.head, mode: MODE.VCOLOR, color: '#9DE0C8' });
    for (let i = 0; i < 5; i++) {
      const a = i * 1.3;
      b.add(new IcosahedronGeometry(0.025, 0), M(Math.sin(a) * 0.1, 0.035, Math.cos(a) * 0.1), { part: p.head, mode: MODE.VCOLOR, color: i % 2 ? '#FF9AC0' : '#FFE066', outline: 0 });
    }
    // Goldfish (anim part: swims around the pivot).
    b.add(new SphereGeometry(1, s(12), s(8)), M(0.12, 0.15, 0, 0, 0, 0, 0.035, 0.05, 0.07), { part: p.anim, mode: MODE.VCOLOR, color: C.fish, outline: 0.5 });
    const tail = new ConeGeometry(0.04, 0.07, 3);
    tail.rotateX(Math.PI / 2);
    b.add(tail, M(0.12, 0.15, -0.085, 0, 0, Math.PI / 2, 1, 1, 1), { part: p.anim, mode: MODE.VCOLOR, color: C.fish, outline: 0.5 });
    for (const side of [-1, 1]) b.add(new SphereGeometry(0.008, s(6), 4), M(0.12 + side * 0.03, 0.162, 0.045), { part: p.anim, mode: MODE.VCOLOR, color: C.ink, outline: 0 });
    return { top: 0.42, anim: 'swim', pivot: [0, 0.15, 0], scale: 1.2 };
  },
  // 12 Cat ears: two triangular ears in the body color with pink insides.
  12: (b, p) => {
    for (const side of [-1, 1]) {
      const ear = new ConeGeometry(0.105, 0.22, 4, 1);
      ear.rotateY(Math.PI / 4);
      b.add(ear, M(side * 0.15, 0.03, -0.02, -0.12, 0, -side * 0.42, 1, 1, 0.42), { part: p.anim, mode: MODE.TINT, color: '#FFFFFF' });
      const inner = new ConeGeometry(0.068, 0.15, 4, 1);
      inner.rotateY(Math.PI / 4);
      b.add(inner, M(side * 0.155, 0.015, 0.012, -0.12, 0, -side * 0.42, 1, 1, 0.2), { part: p.anim, mode: MODE.VCOLOR, color: '#FF9AC0', outline: 0 });
    }
    return { top: 0.18, anim: 'wiggle', pivot: [0, -0.04, 0], scale: 1.4 };
  },
  // 13 Halo: a glowing ring floating above the head.
  13: (b, p) => {
    const ring = new TorusGeometry(0.19, 0.032, s(10), s(40));
    ring.rotateX(Math.PI / 2);
    b.add(ring, M(0, 0.26, 0), { part: p.anim, mode: MODE.UNLIT, color: '#FFE680', outline: 0.6 });
    return { top: 0.34, anim: 'bob', pivot: [0, 0.26, 0] };
  },
  // 14 Mohawk: a row of punk spikes from brow to nape that jiggle as you run.
  14: (b, p) => {
    const spikes: [number, number, number][] = [[0.13, 0.13, 0.055], [0.06, 0.2, 0.06], [-0.02, 0.24, 0.065], [-0.1, 0.22, 0.062], [-0.17, 0.17, 0.055], [-0.22, 0.11, 0.045]];
    const cols = ['#FF4F9A', '#FF5FB0', '#E95BD6', '#C25BEF', '#9B6BFF', '#7F6BFF'];
    spikes.forEach(([z, h, r], i) => {
      const g = new ConeGeometry(r, h, s(10));
      g.translate(0, h / 2, 0);
      const lean = -0.25 - z * 1.5;
      const yb = 0.04 - Math.max(0, Math.abs(z) - 0.08) * 0.5;
      b.add(g, M(0, yb, z, lean, 0, 0, 1, 1, 0.6), { part: p.anim, mode: MODE.VCOLOR, color: cols[i] });
    });
    return { top: 0.27, anim: 'jiggle', pivot: [0, 0.02, 0], scale: 1.3 };
  },
  // 15 Pirate tricorn: three upturned sides, gold trim and a skull badge.
  15: (b, p) => {
    b.add(new SphereGeometry(0.22, s(24), s(10), 0, Math.PI * 2, 0, Math.PI / 2), M(0, -0.03, 0, 0, 0, 0, 1, 0.8, 1), { part: p.head, mode: MODE.VCOLOR, color: C.black });
    const lobes = (a: number): number => 1 + 0.16 * Math.cos(3 * (a + Math.PI / 3) + Math.PI);
    const lift = (a: number, t: number): number => -0.03 + Math.pow(t, 1.6) * (0.08 + 0.1 * (0.5 - 0.5 * Math.cos(3 * (a + Math.PI / 3))));
    b.add(brim(0.18, 0.36, 0.028, s(40), lift, lobes), null, { part: p.head, mode: MODE.VCOLOR, color: '#3A2F45' });
    const edge: [number, number, number][] = [];
    for (let i = 0; i < 48; i++) {
      const a = (i / 48) * Math.PI * 2;
      const r = 0.36 * lobes(a);
      edge.push([Math.sin(a) * r, lift(a, 1) + 0.006, Math.cos(a) * r]);
    }
    b.add(taperedTube(curve(edge, true), 0.016, 0.016, s(60), 5, true), null, { part: p.head, mode: MODE.GOLD, color: C.brass, outline: 0.5 });
    b.add(new SphereGeometry(0.04, s(12), s(10)), M(0, 0.1, 0.235, 0, 0, 0, 1, 1, 0.5), { part: p.head, mode: MODE.VCOLOR, color: C.cream, outline: 0.5 });
    for (const side of [-1, 1]) b.add(new BoxGeometry(0.11, 0.018, 0.012), M(0, 0.06, 0.235, 0, 0, side * 0.6), { part: p.head, mode: MODE.VCOLOR, color: C.cream, outline: 0.4 });
    return { top: 0.2, anim: 'none', pivot: NONE };
  },
  // 16 Paper crown: a zigzag paper band with printed dots (a party-cracker crown).
  16: (b, p) => {
    const peaks = 7;
    const top = (a: number): number => {
      const t = ((a / (Math.PI * 2)) * peaks) % 1;
      return 0.09 + 0.09 * (1 - Math.abs(t - 0.5) * 2);
    };
    b.add(band(0.235, 0.012, peaks * s(8), top), M(0, -0.06, 0, 0.05, 0, 0.06), { part: p.head, mode: MODE.DECAL, color: '#FFE066', color2: '#FF5A4E', pat: PAT.DOTS, around: 14, freq: 18 });
    return { top: 0.14, anim: 'none', pivot: NONE, scale: 1.2 };
  },
};

export function hatIds(): number[] {
  return Object.keys(RECIPES).map(Number);
}

/** Appends hat `id` (0 = none) and returns its placement info. */
export function buildHat(b: PartGeometryBuilder, id: number, parts: HatParts, detail = 1): HatInfo {
  const r = RECIPES[id];
  lod = detail;
  const info = r ? r(b, parts) : { top: 0.05, anim: 'none' as const, pivot: NONE };
  lod = 1;
  return info;
}

/** The crowned leader's gold paper crown (§4.1) and the Crown Grab crown (§4.3), in their own parts. */
export function buildPaperCrown(b: PartGeometryBuilder, part: number, detail = 1): void {
  lod = detail;
  const peaks = 6;
  const top = (a: number): number => {
    const t = ((a / (Math.PI * 2)) * peaks) % 1;
    return 0.08 + 0.1 * (1 - Math.abs(t - 0.5) * 2);
  };
  b.add(band(0.2, 0.014, peaks * s(8), top), null, { part, mode: MODE.GOLD, color: '#FFD24A' });
  for (let i = 0; i < peaks; i++) {
    const a = ((i + 0.5) / peaks) * Math.PI * 2;
    b.add(new SphereGeometry(0.022, s(10), s(8)), M(Math.sin(a) * 0.2, 0.185, Math.cos(a) * 0.2), { part, mode: MODE.VCOLOR, color: i % 2 ? '#FF4F9A' : '#4CB8FF', outline: 0.5 });
  }
}

export function buildRoyalCrown(b: PartGeometryBuilder, part: number, scale = 1, detail = 1): void {
  lod = detail;
  const k = scale;
  b.add(new CylinderGeometry(0.2 * k, 0.19 * k, 0.09 * k, s(28), 1, true), M(0, 0.045 * k, 0), { part, mode: MODE.GOLD, color: '#F2AE1C' });
  b.add(new SphereGeometry(0.17 * k, s(20), s(10), 0, Math.PI * 2, 0, Math.PI / 2), M(0, 0.05 * k, 0, 0, 0, 0, 1, 0.8, 1), { part, mode: MODE.VCOLOR, color: '#C8283C' });
  const ring = new TorusGeometry(0.2 * k, 0.018 * k, s(8), s(32));
  ring.rotateX(Math.PI / 2);
  b.add(ring, M(0, 0.0, 0), { part, mode: MODE.GOLD, color: '#D99412' });
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const g = new ConeGeometry(0.06 * k, 0.16 * k, 4);
    g.rotateY(Math.PI / 4);
    b.add(g, M(Math.sin(a) * 0.19 * k, 0.15 * k, Math.cos(a) * 0.19 * k, 0, a, 0, 1, 1, 0.45), { part, mode: MODE.GOLD, color: '#F2AE1C' });
    b.add(new SphereGeometry(0.028 * k, s(10), s(8)), M(Math.sin(a) * 0.19 * k, 0.24 * k, Math.cos(a) * 0.19 * k), { part, mode: MODE.GOLD, color: '#FFE07A' });
    b.add(new IcosahedronGeometry(0.03 * k, 0), M(Math.sin(a + 0.63) * 0.205 * k, 0.045 * k, Math.cos(a + 0.63) * 0.205 * k), { part, mode: MODE.VCOLOR, color: i % 2 ? '#4CB8FF' : '#FF3E6C', outline: 0.4 });
  }
}
